import { useCallback, useEffect, useRef, useState } from "react";

import { groupMenuItems } from "@/components/groups/groupMenu";
import { useGroups } from "@/components/groups/useGroups";
import { useDialogs } from "@/components/shell/Dialogs";
import { TopBar } from "@/components/shell/TopBar";
import { setFlag, setSuspended, addTags } from "@/lib/cards";
import { useCollectionChanged } from "@/lib/useCollectionChanged";
import { useRefreshOnFocus } from "@/lib/useRefreshOnFocus";
import {
  answerCard,
  endReview,
  endReviewBeacon,
  extendNewLimit,
  reviewFinished,
  reviewLanding,
  startReview,
  reviewPosition,
  undoReview,
  type ReviewCard,
  type ReviewFinished,
  type ReviewLanding,
  type ReviewPosition,
} from "@/lib/review";

import { Finished } from "./Finished";
import { Landing } from "./Landing";
import { Session, SessionChrome } from "./Session";
import { imageModeOptions, loadImageMode, saveImageMode, type ImageMode } from "./sessionRules";

/**
 * The Review surface (日本-185) — landing, session, finished.
 *
 * **Which one is on screen is decided by two things and no local mode flag**: `deck` (the URL's
 * `review.deck`) says whether a sitting is open, and `position.card === null` says whether it has
 * run out. That is what makes a mid-session reload land back in the session — the queue is the
 * server's, so the URL only has to carry which deck.
 */

interface Props {
  isPhone: boolean;
  /** The phone tab bar. **Rendered on the landing and NOT during a session** — `✕` is the way out
   *  of a sitting, and a tab bar under a grading button is a mis-tap that leaves the session. */
  footer?: React.ReactNode;
  deck: string | null;
  onDeckChange: (deck: string | null, opts?: { replace?: boolean }) => void;
  onOpenStats: () => void;
  onOpenNote: (noteId: number) => void;
  onCollectionChanged: () => void;
}

export function Review({
  isPhone,
  footer,
  deck,
  onDeckChange,
  onOpenStats,
  onOpenNote,
  onCollectionChanged,
}: Props) {
  const dialogs = useDialogs();
  const [landing, setLanding] = useState<ReviewLanding | null>(null);
  const [position, setPosition] = useState<ReviewPosition | null>(null);
  // Confusion groups for the card on screen (日本-601): the submenu's ticks, and `G`.
  const groupState = useGroups(position?.card ? [position.card.noteId] : []);
  const [finished, setFinished] = useState<ReviewFinished | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * A grade that did not land (日本-347).
   *
   * **Cleared by the next grade that lands, not by the recovery re-read** — those look like the same
   * moment and are not: the re-read runs through `apply` immediately after the failure, so clearing
   * there would erase the notice in the tick it was set. There is no dismiss button and none is
   * wanted; answering the card again is the dismissal.
   */
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * The counts on screen belong to a sitting that is over (日本-404).
   *
   * **True only on the exit that had no `7e` to seed from** — Back or `Esc` out of a live sitting.
   * It is deliberately not a general loading flag: a landing being *refreshed* on focus is not a
   * landing that is missing, and skeletoning one that is already drawn and still true would be a
   * flicker bought for nothing. `Landing` renders the figures as placeholders while it holds; the
   * deck names, the rotation counts and the buttons are structure and never wait.
   */
  const [countsPending, setCountsPending] = useState(false);
  /** The same flag a handler can read *synchronously*. Two `↑` in one frame both see `busy` as
   *  false through state, because React has not re-rendered between them. */
  const busyRef = useRef(false);
  busyRef.current = busy;
  /**
   * `finished`, reachable from the deck-transition effect below without joining its dep list.
   *
   * A dep would re-run that effect every time `7e`'s figures arrive; the `previousDeck` guard makes
   * the re-run harmless, but an effect whose deps say it is about `finished` and whose body is about
   * leaving is the kind of thing somebody later "fixes" in the wrong direction.
   */
  const finishedRef = useRef<ReviewFinished | null>(null);
  finishedRef.current = finished;
  const [imageMode, setImageMode] = useState<ImageMode>(() =>
    loadImageMode(typeof window === "undefined" ? null : window.localStorage),
  );

  const imageModes = imageModeOptions();

  // --- entering and leaving --------------------------------------------------------------------

  /**
   * A notice belongs to the sitting that produced it.
   *
   * **`Review` does not unmount between sittings** — `deck` merely changes — so without this a red
   * "That grade didn't save" from one deck rides into the next sitting's chrome, where it outranks
   * the countdown, until a successful grade happens to clear it. Every other piece of per-sitting
   * state is reset by one of the two effects below; this is the one that belongs to neither, because
   * it has to go in *both* directions.
   */
  useEffect(() => setNotice(null), [deck]);

  /**
   * The landing's deck counts, re-read whenever they could have moved (日本-388).
   *
   * It used to be a `[deck]`-only effect, so the numbers were fetched once and held until `deck`
   * changed — and `deck` does not change overnight. Leaving the app on this screen and coming back
   * in the morning showed yesterday's queue while the rail's badge, which has had both of these
   * hooks since it was hoisted into `useDeckCounts`, showed today's. Navigating away and back
   * "fixed" it only because `App` renders this surface inside `if (active === "review")`, so a tab
   * change unmounts it and the effect runs again at mount.
   *
   * **Focus is the half that fixes that ticket.** The morning case is a day rollover: nothing wrote
   * to the collection, so no `collectionChanged` frame is ever published and the event hook alone
   * would never have fired. `useCollectionChanged` is here for the other case `useDeckCounts`
   * names — a 03:00 job moving the numbers, which produces no focus event to hang a refresh on.
   * **The two hooks that drive it are declared below the exit effect**, and that placement is the
   * subject of `endSeed`'s own note.
   *
   * **The `deck` guard is load-bearing.** Unlike the effect it replaces, a hook is live for as long
   * as the component is mounted — and this one does not unmount between sittings, `deck` merely
   * changes. Without the guard, switching tabs mid-card would fetch the landing on the same worker
   * thread the next card is coming from. Refreshing *during* a sitting is deliberately out of
   * scope: a sitting holds this handle's syncing open so `col.undo()` works, and what the counters
   * say is the server's business while it does.
   */
  const landingGeneration = useRef(0);
  /**
   * The generation an in-flight `POST /api/review/end` will answer for, or `0` (日本-404).
   *
   * **It is a suppression token, and what it suppresses is two requests for an answer already on
   * its way.** Leaving fires `onCollectionChanged()` — synchronous, so `useCollectionChanged`'s
   * subscriber runs inside the exit effect — and then `useRefreshOnFocus`'s `[deck]` effect runs on
   * the key change, unconditionally by design. Before this, both called `readLanding`, so every exit
   * issued *two* `GET /api/review/decks`, and both queued on the server's single interactive worker
   * behind `/end`'s sync flush. `/end` now answers with the deck list itself, computed after that
   * flush, so those two reads would be a slower way of asking a question already answered.
   *
   * Comparing against `landingGeneration` rather than a bare boolean is what makes it safe: anything
   * that legitimately supersedes the exit bumps the generation, and the token stops matching.
   */
  const endSeed = useRef(0);
  const readLanding = () => {
    if (deck) return;
    // The exit's own answer is newer than anything this could fetch, and it is already in flight.
    if (endSeed.current !== 0 && endSeed.current === landingGeneration.current) return;
    const issued = ++landingGeneration.current;
    reviewLanding()
      .then((result) => {
        if (issued === landingGeneration.current) setLanding(result);
      })
      .catch(() => {
        // **Only ever fabricate the empty landing on a FIRST failure.** It exists so a hub that
        // cannot be read renders the screen's own empty state instead of nothing at all, which is
        // what the `landing &&` gate below would otherwise give you. Once the read repeats, writing
        // it unconditionally would let one flaky focus refresh replace a good deck list with "no
        // decks" — `useDeckCounts` and `useHistory` both keep the last known answer for this
        // reason, and now so does this.
        if (issued === landingGeneration.current) {
          setLanding((current) => current ?? { decks: [], dueTotal: 0, newTotal: 0 });
        }
      });
  };

  /**
   * まだまだ — take on five more of one subdeck's new cards today (日本-451).
   *
   * **Not optimistic, and that is a departure from `useReading`'s rule rather than an oversight.**
   * That rule exists because the reading loop is hotkey-paced and must not stall on a round trip;
   * this is one deliberate press on a screen nobody is racing. And the figure it changes is not
   * derivable here: the server lifts the deck AND its ancestors, then recomputes with the tightest
   * per-day limit down the chain, so a client-side +5 would be a guess that is wrong whenever
   * another deck's limit is the binding one.
   *
   * **The response IS the landing** (`/extend` answers `7a`, on `/end`'s precedent), so this costs
   * one request rather than a write plus a re-read queued behind it on the server's single
   * interactive worker.
   *
   * The generation is bumped so a slow refresh already in flight cannot land on top of the answer.
   */
  const [extending, setExtending] = useState<string | null>(null);
  const onExtend = useCallback(
    (name: string) => {
      // One at a time. The write lifts ancestors, so two presses in flight would each be computing
      // from figures the other is about to move.
      if (extending) return;
      setExtending(name);
      // Cleared on the press, not only on the answer: `deck` never changes on the landing, so the
      // effect that scopes a notice to its sitting cannot fire here and a failed press would
      // otherwise leave its line on screen for as long as the tab stayed open. A retry that works
      // has to be able to take it back.
      setNotice(null);
      const issued = ++landingGeneration.current;
      extendNewLimit(name)
        .then((result) => {
          if (issued === landingGeneration.current) setLanding(result);
        })
        .catch(() => {
          // **Says so rather than failing silently**, because the failure is otherwise invisible:
          // the counts simply do not move, which is indistinguishable from a deck that had nothing
          // left to take on. A 409 is the reachable case — a sitting open in another tab.
          setNotice("新規を追加できませんでした");
        })
        .finally(() => setExtending((current) => (current === name ? null : current)));
    },
    [extending],
  );

  /**
   * **A sitting ends when `deck` goes from a value to null, and that is the only place it ends**
   * (日本-385).
   *
   * It used to end in `leave`, which is reached from `✕`, `Esc`, the idle expiry and `7e`'s Done —
   * and from nothing else. **Android's Back is nothing else.** It is a `popstate`, so
   * `useHistoryNav` sets `review.deck` to null and this component simply re-renders on the landing;
   * no `leave`, and no `pagehide` either, because an in-app navigation never unloads the document.
   * The sitting stayed open, its answers stayed in the server's replica, and the hold went on
   * suppressing syncing for *every* surface until the deadline expired — which self-heals only if
   * something happens to read, and on a phone that gets locked nothing does.
   *
   * Keyed on the transition rather than on `leave` because every exit already produces it, so one
   * path covers all five and there is no second one to drift. Two properties fall out of keying it
   * on the **value** rather than on unmount, and both are load-bearing:
   *
   * - **Editing a card is deliberately not leaving** (Alex, 13 Aug 2026). `App`'s `onOpenNote`
   *   patches `tab` and `cards` and leaves `review.deck` alone, so this component unmounts with the
   *   deck still set and the transition never happens — the sitting, its tally and Anki's undo queue
   *   all survive the trip to Cards. An unmount-cleanup version would end the sitting on every
   *   `Card ⋯ → Edit`, which is exactly the thing this must not do.
   * - **A reload mid-session mounts with `deck` set**, so the ref starts on the live deck and
   *   nothing fires; `start` resumes the sitting as it always did.
   *
   * Switching deck at `7e` is `A → B` rather than `→ null` and does not flush here. It does not need
   * to: `start` on a different deck runs `col.decks.select` through `write`, which syncs
   * unconditionally — see `AnkiCollection.hold_sync`, which exempts `read`, `write` and
   * `read_media` from the hold.
   *
   * **This is also where the landing's counts are replaced, and the two halves are one act**
   * (日本-404). `Review` does not unmount between sittings, so `landing` survives the sitting and
   * used to render as if current — the pre-sitting `要復習 71` sat on screen for two round trips,
   * directly above a heatmap that was correctly drawing a skeleton, because *its* state lives in
   * `Landing` and does unmount. There are two sources and the choice between them is exactly the
   * question of what the reader has already been shown:
   *
   * - **From `7e`, when there is one.** `GET /finished` has always computed a whole landing to pick
   *   `nextDeck` and thrown the rest away; it carries it now, read off the held replica, so it
   *   already counts this sitting. Seeded here rather than when it arrives, because the reader can
   *   `↑` off `7e` and keep answering — applied early it would describe a sitting still running.
   * - **From `POST /end` otherwise**, which is every Back and `Esc` out of a live sitting. Until it
   *   answers there is nothing true to draw, so `countsPending` puts placeholders under the figures
   *   rather than leaving last sitting's numbers up.
   *
   * **The old ordering rule is now structural and no longer depends on this line.** It used to say:
   * declared above the landing read, because React runs effects in declaration order and the landing
   * is a `read_fast` that `_ensure_fresh` short-circuits while the hold is in force — read first, it
   * describes the un-flushed replica and silently omits reviews another device did since the sitting
   * began. True, and one refactor from being broken. `end()` on the server now flushes and *then*
   * computes the landing in the same call, so the freshness is a property of the route. What still
   * matters here is only that `endSeed` is claimed before the two landing hooks below can fire.
   */
  const previousDeck = useRef(deck);
  useEffect(() => {
    const previous = previousDeck.current;
    previousDeck.current = deck;
    if (!previous || deck) return;

    const issued = ++landingGeneration.current;
    endSeed.current = issued;

    const seed = finishedRef.current;
    if (seed) setLanding(seed.landing);
    else setCountsPending(true);

    endReview()
      .then((fresh) => {
        // **The generation check comes FIRST, and it guards `endSeed` as well as the state.** A
        // superseded exit — leave, start a deck, leave again inside one round trip — must not
        // release the token the exit that replaced it is relying on: with it released, a focus or
        // a collection event issues the `GET /decks` the token exists to suppress, that read bumps
        // the generation, and the live `/end` then discards its own answer on this very line —
        // leaving `countsPending` true and the deck card drawing placeholders until the next exit.
        if (issued !== landingGeneration.current) return;
        endSeed.current = 0;
        setLanding(fresh);
        setCountsPending(false);
      })
      .catch(() => {
        // The hub could not be reached, or the sitting was already gone. **Release the token and
        // read**, or the suppression outlives the request it was protecting and the counts stay
        // pending until the next focus — a worse screen than the stale one this replaced. Guarded
        // on the generation for the reason above: a superseded failure must not clear a live exit's
        // pending state, and must not issue the read that exit's token is suppressing.
        if (issued !== landingGeneration.current) return;
        endSeed.current = 0;
        setCountsPending(false);
        readLanding();
      });

    onCollectionChanged();
    // `readLanding` is re-created every render and adding it here would re-run the exit on every
    // one of them; the `previousDeck` guard makes that harmless, but an effect that re-runs for a
    // reason unrelated to its subject is how the `finished` seed above would get moved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck, onCollectionChanged]);

  /**
   * Leaving a sitting clears what belonged to it. **Reset, not refresh** — this and the landing
   * read below were one effect until 日本-388, and only this half was ever about `deck` changing.
   *
   * `position` in particular has to go on the way out: `if (!deck)` is checked first in the render
   * below and stops guarding the moment a deck is chosen, so a stale `position` would draw the
   * previous sitting's card — or its `7e` — for the round trip until `start` answers.
   */
  useEffect(() => {
    if (deck) return;
    setPosition(null);
    setFinished(null);
  }, [deck]);

  useRefreshOnFocus(readLanding, deck);
  useCollectionChanged(readLanding);

  useEffect(() => {
    if (!deck) return;
    let live = true;
    startReview(deck)
      .then((result) => live && apply(result))
      .catch(() => {
        // A deck that no longer exists, or a hub that cannot be read. Falling back to the landing
        // beats a session frame with no card and no explanation.
        if (live) onDeckChange(null, { replace: true });
      });
    return () => {
      live = false;
    };
    // `apply` is stable enough for this and re-running on every card would restart the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck]);

  const apply = useCallback((next: ReviewPosition) => {
    setPosition(next);
  }, []);

  /** The queue ran out. `7e` is fetched rather than derived — the counters and what is due
   *  elsewhere are the server's, and the sitting's elapsed time is its own. */
  useEffect(() => {
    if (!position || position.card !== null) {
      setFinished(null);
      return;
    }
    let live = true;
    reviewFinished()
      .then((result) => live && setFinished(result))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [position]);

  /**
   * A tab closed mid-sitting never reaches `/end`, and until something flushes, the session's
   * answers are in the server's replica and the collection cannot sync. The server's deadline is
   * the guarantee; this makes the common case prompt rather than minutes late.
   *
   * `pagehide` rather than `beforeunload`: the latter does not fire reliably on mobile, which is
   * exactly where a tab gets discarded without warning.
   */
  useEffect(() => {
    if (!deck) return;
    const flush = () => endReviewBeacon();
    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, [deck]);

  const leave = useCallback(() => {
    // `replace`, not push: the session is GONE rather than behind you, so Back must not walk into
    // a sitting that has been ended. Same argument `logTabs` makes about closing a reading tab.
    //
    // The flush is the effect above, not a call here — this only has to make the transition happen.
    onDeckChange(null, { replace: true });
  }, [onDeckChange]);

  // --- the two writes --------------------------------------------------------------------------

  const answer = useCallback(
    async (card: ReviewCard, passed: boolean, tookMs: number, revealMs: number | null) => {
      if (busyRef.current) return;
      setBusy(true);
      try {
        apply(await answerCard(card, passed, tookMs, revealMs));
        // **Cleared by a grade that landed, not by a position that came back.** The recovery below
        // re-reads through the same `apply`, so clearing there would erase the notice in the tick it
        // was set — which is exactly the silence this is fixing.
        setNotice(null);
      } catch {
        // **Never a blind retry, and never a silent one either** (日本-347).
        //
        // Re-reading rather than re-sending is the old rule and it stands: a grade that may have
        // landed must not be sent twice, which is exactly how one keystroke becomes two reviews.
        //
        // **What is new is that every failure says so, the conflict included.** The first cut of
        // this kept the notice for everything *except* a `ReviewConflict`, on the reasoning that a
        // conflict is visible on its own because the card or the counts move. That reasoning is
        // wrong twice. A lapsed sync hold is mapped to 409 as well — deliberately, because the
        // recovery is the same — so it arrived here as a `ReviewConflict` and was silenced, which
        // is precisely the dropped-grade-with-no-feedback this ticket exists to fix. And even for a
        // real stale card, "the counts move" is not reliable: a double-tapped Pass on a card that
        // comes back as *Again* redraws the same word with the same buttons.
        //
        // So there is one rule and one path. The wording is true of every case that reaches here —
        // a guard that refused, a hold that lapsed, a network drop: the grade did not save.
        setNotice("記録できませんでした");
        await reviewPosition()
          .then(apply)
          .catch(() => {
            /* the re-read failed too; the notice is the only thing on screen that is still true */
          });
      } finally {
        setBusy(false);
      }
    },
    [apply],
  );

  const undo = useCallback(async () => {
    // The server claims its entry before awaiting, so two presses can no longer undo one card
    // twice — but they would still cost two round trips and two re-renders. This is the cheap half.
    if (busyRef.current) return;
    setBusy(true);
    try {
      apply(await undoReview());
    } catch {
      // Usually nothing to undo, which the button already disables for — but it is also the other
      // `write_local` caller, so it can fail the same way an answer can. Re-read either way; a
      // silent no-op on a key the reader deliberately pressed is what this is fixing.
      await reviewPosition()
        .then(apply)
        .catch(() => {});
    } finally {
      setBusy(false);
    }
  }, [apply]);

  // --- the card menu's actions -----------------------------------------------------------------

  /** Re-read after a write that changed the card without grading it, so the flag chip, the tags
   *  and a suspension all show without the reader pressing anything. */
  const refresh = useCallback(() => {
    reviewPosition().then(apply).catch(() => {});
    onCollectionChanged();
  }, [apply, onCollectionChanged]);

  const suspend = useCallback(
    async (card: ReviewCard, wholeNote: boolean) => {
      if (wholeNote) {
        const ok = await dialogs.confirm({
          // The headword is user content and is wrapped by the verb rather than translated.
          title: "ノート全体を停止しますか？",
          body: `${card.headword} から作られたカードはすべて、停止を解除するまでキューから外れます。`,
          confirmLabel: "ノートを停止",
          tone: "danger",
        });
        if (!ok) return;
      }
      // Card ids either way, because suspension IS a card property — the whole-note case simply
      // sends every card the note has, which the DTO carries for exactly this.
      await setSuspended(wholeNote ? card.noteCardIds : [card.cardId], true);
      refresh();
    },
    [dialogs, refresh],
  );

  const flag = useCallback(
    async (card: ReviewCard, value: number) => {
      await setFlag([card.cardId], value);
      refresh();
    },
    [refresh],
  );

  const tag = useCallback(
    async (card: ReviewCard) => {
      const value = await dialogs.prompt({
        title: "タグを追加",
        label: card.headword,
        hint: "スペース区切りで複数指定できます。",
        submitLabel: "追加",
      });
      if (!value?.trim()) return;
      await addTags([card.noteId], value.trim());
      refresh();
    },
    [dialogs, refresh],
  );

  // --- what is on screen -------------------------------------------------------------------------

  if (!deck) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {/* **The landing keeps a top bar, and only the landing.** The Mobile canvas says so
            directly — *"復習 · the landing keeps its top bar here, because the tab bar is not a
            title"* — and it is the one frame of six that carries the mark, which is why `mark` is
            set here and nowhere else. Inside a sitting there is no bar at all: `SessionChrome` is
            the surface's own, and two rows of chrome above a card is most of the reading area. */}
        {isPhone && <TopBar title="復習" destinationId="review" mark />}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {landing && (
            <Landing
              landing={landing}
              pending={countsPending}
              deckForHistory={landing.decks[0]?.name ?? null}
              onStudy={(name) => onDeckChange(name)}
              onExtend={onExtend}
              extending={extending}
              // `notice` reaches both halves of the surface: `SessionChrome` draws it inside a
              // sitting, and this draws it on the landing, which is the only place まだまだ can be
              // pressed from. Without it a failed extend wrote a string nothing rendered.
              notice={notice}
              onOpenStats={onOpenStats}
            />
          )}
        </div>
        {footer}
      </div>
    );
  }

  if (position?.card === null) {
    // **Inside the session frame, chrome and all.** The design is explicit: finishing must not
    // bounce you back to the landing, because a silent bounce reads as a crash — and the bar is
    // what says you are still in the sitting it is reporting on. Drawn here rather than inside
    // `Finished` so the one bar belongs to the session either way.
    //
    // **The chrome is drawn before `7e`'s figures arrive**, not after (日本-347). `reviewFinished`
    // is its own request, and rendering `null` until it lands dropped the last Pass of a session to
    // a blank screen for a whole round trip — the same "did that do anything?" silence as the delay
    // this ticket is about, and on the one card where the reader is most watching for a result. The
    // bar already says `110 / 110`; it can say it a beat early.
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <SessionChrome position={position} isPhone={isPhone} onEnd={leave} />
        {finished && (
          <Finished
            finished={finished}
            isPhone={isPhone}
            canUndo={position.canUndo}
            onStudyNext={(next) => onDeckChange(next)}
            onUndo={undo}
            onDone={leave}
            onOpenStats={onOpenStats}
          />
        )}
      </div>
    );
  }

  if (!position) return null;

  return (
    <Session
      position={position}
      isPhone={isPhone}
      imageMode={imageMode}
      imageModes={imageModes}
      busy={busy}
      onImageMode={(mode) => {
        setImageMode(mode);
        saveImageMode(typeof window === "undefined" ? null : window.localStorage, mode);
      }}
      onAnswer={answer}
      onUndo={undo}
      onEnd={leave}
      onEdit={(card) => onOpenNote(card.noteId)}
      onSuspend={suspend}
      onFlag={flag}
      onAddTag={tag}
      onInfo={(card) => onOpenNote(card.noteId)}
      notice={notice}
      groupItems={(card) =>
        groupMenuItems(groupState.groups, [card.noteId], groupState.membership, groupState.actions)
      }
    />
  );
}
